import { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { stripHtml } from "@/lib/sanitize";
import { useMetaTags } from "@/hooks/useMetaTags";
import Header from "@/components/Header";
import SearchBar from "@/components/SearchBar";
import LoadingState from "@/components/LoadingState";
import ResultCardSkeleton from "@/components/ResultCardSkeleton";
import ClarifyingQuestion from "@/components/ClarifyingQuestion";
import ResultCard from "@/components/ResultCard";
import NewsletterCapture from "@/components/NewsletterCapture";
import SignupNudge from "@/components/SignupNudge";
import TrackTopicInline from "@/components/TrackTopicInline";
import { MockEpisode, isVagueQuery, getClarifyingOptions } from "@/data/mockData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getStoredTuning } from "@/components/admin/AdminScoreTuning";
import { useAuth } from "@/contexts/AuthContext";
import { useSavedEpisodes } from "@/hooks/useSavedEpisodes";
import TiltCard from "@/components/TiltCard";
import { useUserFeedback } from "@/hooks/useUserFeedback";
import { Zap } from "lucide-react";
import { useJsonLd } from "@/hooks/useJsonLd";
import SearchResultsIntroModal from "@/components/SearchResultsIntroModal";
import SearchResultsFeedback from "@/components/SearchResultsFeedback";
import AnsweredQuestionCallout, { MatchedAnswer, getMatchedAnswerTier } from "@/components/AnsweredQuestionCallout";

export default function SearchResults() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchSpotlight, setSearchSpotlight] = useState(false);
  const headerWrapperRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // Only inert the header during spotlight. The <main> stays interactive
    // so mobile users can still scroll results with their thumb — marking
    // <main> inert was trapping touch scrolling because hasQuery (and thus
    // spotlight) latches true as soon as the textarea gets focus.
    const el = headerWrapperRef.current;
    if (!el) return;
    if (searchSpotlight) el.setAttribute("inert", "");
    else el.removeAttribute("inert");
  }, [searchSpotlight]);

  const query = searchParams.get("q") || "";
  const [loading, setLoading] = useState(true);
  const [streamingMore, setStreamingMore] = useState(false);
  const [showClarify, setShowClarify] = useState(false);
  const [clarifyOptions, setClarifyOptions] = useState<string[]>([]);
  const [clarifyOptionsLoading, setClarifyOptionsLoading] = useState(false);
  const [results, setResults] = useState<MockEpisode[]>([]);
  const [finalQuery, setFinalQuery] = useState(query);
  const [theme, setTheme] = useState("");
  const [matchedAnswer, setMatchedAnswer] = useState<MatchedAnswer | null>(null);
  const [nudge, setNudge] = useState<{ open: boolean; action: "save" | "like" | "dislike" }>({ open: false, action: "save" });

  const { user } = useAuth();
  const { savedIds, toggleSave } = useSavedEpisodes();
  const { feedbackMap, toggleFeedback } = useUserFeedback(finalQuery);
  const [queryPopularity, setQueryPopularity] = useState(0);

  // Fetch how many times this query has been searched (popularity signal for FAQ JSON-LD).
  useEffect(() => {
    if (!query) return;
    let cancelled = false;
    (async () => {
      const { count } = await supabase
        .from("search_logs")
        .select("id", { count: "exact", head: true })
        .ilike("query", query.trim());
      if (!cancelled) setQueryPopularity(count || 0);
    })();
    return () => { cancelled = true; };
  }, [query]);

  // Build FAQPage JSON-LD when we have a high-signal answer.
  // Triggers when: results exist, top result has an AI summary, and the query is
  // either question-shaped OR has been searched 3+ times (popular).
  const faqJsonLd = useMemo(() => {
    if (loading || showClarify || results.length === 0) return null;
    const top = results[0] as any;
    const summary = (top?.summary || top?.aiSummary || "").trim();
    if (!summary || summary.length < 60) return null;
    const q = finalQuery.trim();
    const isQuestionShaped = /\?$|^(how|what|why|when|where|which|who|should|can|is|are|do|does)\b/i.test(q);
    const isPopular = queryPopularity >= 3;
    if (!isQuestionShaped && !isPopular) return null;

    const cleanQuestion = q.endsWith("?") ? q : `${q}?`;
    const sourceLine = top.slug
      ? ` Source: "${stripHtml(top.episodeTitle)}" on ${top.podcastName} — https://askthepods.com/episode/${top.slug}`
      : "";
    const answer = `${stripHtml(summary)}${sourceLine}`.slice(0, 1200);

    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: cleanQuestion,
          acceptedAnswer: {
            "@type": "Answer",
            text: answer,
          },
        },
      ],
    };
  }, [loading, showClarify, results, finalQuery, queryPopularity]);

  useJsonLd("faq-search", faqJsonLd);

  // SearchResultsPage + ItemList JSON-LD so bots understand this page is a
  // ranked recommendation set and can cite the source episodes directly.
  const searchPageJsonLd = useMemo(() => {
    if (loading || showClarify || results.length === 0 || !finalQuery) return null;
    const SITE_URL = "https://askthepods.com";
    const pageUrl = `${SITE_URL}/search?q=${encodeURIComponent(finalQuery)}`;

    const itemListElement = results.slice(0, 10).map((ep: any, i: number) => {
      const epUrl = ep.slug ? `${SITE_URL}/episode/${ep.slug}` : ep.episodeUrl;
      const podcastEpisode: Record<string, unknown> = {
        "@type": "PodcastEpisode",
        name: stripHtml(ep.episodeTitle || ""),
        url: epUrl,
        ...(ep.publishDate ? { datePublished: ep.publishDate } : {}),
        ...(ep.durationSeconds
          ? { timeRequired: `PT${Math.round(ep.durationSeconds / 60)}M` }
          : {}),
        ...(ep.summary
          ? { description: stripHtml(ep.summary).slice(0, 500) }
          : {}),
        ...(ep.audioUrl
          ? { associatedMedia: { "@type": "AudioObject", contentUrl: ep.audioUrl } }
          : {}),
        partOfSeries: {
          "@type": "PodcastSeries",
          name: ep.podcastName,
          ...(ep.podcastArtwork ? { image: ep.podcastArtwork } : {}),
        },
        ...(ep.guestName
          ? { actor: { "@type": "Person", name: ep.guestName } }
          : {}),
      };
      return {
        "@type": "ListItem",
        position: i + 1,
        url: epUrl,
        item: podcastEpisode,
      };
    });

    return {
      "@context": "https://schema.org",
      "@type": "SearchResultsPage",
      url: pageUrl,
      name: `Best podcast episodes for "${finalQuery}" — AskThePods`,
      description: `AI-ranked ecommerce podcast episodes that answer: ${finalQuery}. Each result is a cited recommendation with confidence score, summary, and link to the source episode.`,
      isPartOf: { "@type": "WebSite", "@id": `${SITE_URL}/#website`, url: SITE_URL },
      about: { "@type": "Thing", name: finalQuery },
      mainEntity: {
        "@type": "ItemList",
        name: `Top ${results.length} ecommerce podcast episodes for "${finalQuery}"`,
        numberOfItems: results.length,
        itemListOrder: "https://schema.org/ItemListOrderDescending",
        itemListElement,
      },
    };
  }, [loading, showClarify, results, finalQuery]);

  useJsonLd("search-results-page", searchPageJsonLd);

  useMetaTags({
    title: finalQuery ? `${finalQuery} — AskThePods` : "Search — AskThePods",
    description: results[0]
      ? stripHtml((results[0] as any).summary || "").slice(0, 155)
      : "Search ecommerce podcast episodes for Shopify, DTC, Amazon, and more.",
    url: typeof window !== "undefined" ? window.location.href : undefined,
  });

  const doSearch = async (searchQuery: string, attempt = 1) => {
    // Stale-while-revalidate with a fresh-window short-circuit:
    //  - FRESH hit (< 5 min, same tuning): paint and skip the network entirely.
    //  - STALE hit: paint instantly, refetch in the background to keep accurate.
    //  - MISS: standard loading flow.
    const tuning = getStoredTuning();
    let usedCache = false;
    let cacheFreshness: "fresh" | "stale" | "miss" = "miss";
    let tuningFingerprint = "0";
    if (attempt === 1) {
      try {
        const { getCachedSearch, fingerprintTuning } = await import("@/lib/searchCache");
        tuningFingerprint = fingerprintTuning(tuning);
        const cached = getCachedSearch<{
          results: MockEpisode[];
          theme?: string;
          matched_answer?: MatchedAnswer | null;
        }>(searchQuery, tuningFingerprint);
        if (cached?.data?.results?.length) {
          setResults(cached.data.results);
          setTheme(cached.data.theme || "");
          setMatchedAnswer(cached.data.matched_answer || null);
          setFinalQuery(searchQuery);
          setLoading(false);
          usedCache = true;
          cacheFreshness = cached.freshness;
          // Fresh hit → trust it, no revalidation. Done.
          if (cached.freshness === "fresh") {
            try {
              const { recordMetric } = await import("@/lib/rum");
              recordMetric({
                metric_name: "search_latency",
                value: 0,
                context: { cache_hit: true, freshness: "fresh", age_ms: cached.ageMs },
              });
            } catch { /* telemetry never throws */ }
            return;
          }
        }
      } catch { /* cache is best-effort */ }
    }

    if (!usedCache) setLoading(true);
    if (attempt === 1 && !usedCache) {
      setResults([]);
      setMatchedAnswer(null);
      setFinalQuery(searchQuery);
    }
    setStreamingMore(false);

    // If a prefetch (triggered by the submit/click that brought us here) is
    // already in flight, await it instead of starting a duplicate request.
    if (!usedCache && attempt === 1) {
      try {
        const { getPendingSearch } = await import("@/lib/prefetchSearch");
        const pending = getPendingSearch(searchQuery, tuningFingerprint);
        if (pending) {
          const payload = await pending;
          if (payload?.results?.length) {
            setResults(payload.results);
            setTheme(payload.theme || "");
            setMatchedAnswer(payload.matched_answer || null);
            setFinalQuery(searchQuery);
            setLoading(false);
            try {
              const { recordMetric } = await import("@/lib/rum");
              recordMetric({
                metric_name: "search_latency",
                value: Math.round(performance.now() - performance.now()),
                context: { cache_hit: false, prefetched: true },
              });
            } catch { /* telemetry never throws */ }
            return;
          }
        }
      } catch { /* prefetch is best-effort */ }
    }

    const t0 = performance.now();
    try {
      const { getSessionId } = await import("@/lib/sessionId");

      // Direct streaming fetch — `supabase.functions.invoke` buffers, so we hit
      // the function URL ourselves and parse NDJSON events as they arrive.
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
      const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token || SUPABASE_KEY;

      const resp = await fetch(`${SUPABASE_URL}/functions/v1/semantic-search`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_KEY,
        },
        body: JSON.stringify({
          query: searchQuery,
          tuning,
          session_id: getSessionId(),
          stream: true,
        }),
      });

      if (!resp.ok || !resp.body) {
        const errBody = await resp.text().catch(() => "");
        throw new Error(`Search HTTP ${resp.status}: ${errBody.slice(0, 200)}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finalEvent: any = null;
      let sawPartial = false;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let nl: number;
        while ((nl = buffer.indexOf("\n")) >= 0) {
          const line = buffer.slice(0, nl).trim();
          buffer = buffer.slice(nl + 1);
          if (!line) continue;
          try {
            const evt = JSON.parse(line);
            if (evt.type === "partial" && Array.isArray(evt.results) && evt.results.length) {
              sawPartial = true;
              // When revalidating a stale cache hit, don't overwrite the
              // already-painted full set with a partial; wait for `final`.
              if (!usedCache) {
                setResults(evt.results);
                setLoading(false);
                setStreamingMore(true);
              }
            } else if (evt.type === "final") {
              finalEvent = evt;
              if (Array.isArray(evt.results)) setResults(evt.results);
              setTheme(evt.theme || "");
              setMatchedAnswer(evt.matched_answer || null);
              setLoading(false);
              setStreamingMore(false);
            }
          } catch { /* tolerate partial lines */ }
        }
      }

      // Record client-perceived search latency (RUM)
      try {
        const { recordMetric } = await import("@/lib/rum");
        recordMetric({
          metric_name: "search_latency",
          value: Math.round(performance.now() - t0),
          context: {
            results_count: finalEvent?.results?.length ?? 0,
            attempt,
            cache_hit: usedCache,
            freshness: cacheFreshness,
            streamed: true,
            saw_partial: sawPartial,
          },
        });
      } catch { /* telemetry never throws */ }

      if (finalEvent?.results?.length) {
        // Persist for next visit/back-nav, keyed by tuning fingerprint so a
        // tuning change naturally invalidates the prior entry.
        try {
          const { setCachedSearch } = await import("@/lib/searchCache");
          setCachedSearch(searchQuery, {
            results: finalEvent.results,
            theme: finalEvent.theme || "",
            matched_answer: finalEvent.matched_answer || null,
          }, { fingerprint: tuningFingerprint });
        } catch { /* cache writes never throw */ }
      } else if (!sawPartial && !usedCache) {
        toast.info("No matching episodes found. Try a different query.");
        setResults([]);
        setMatchedAnswer(null);
      }
    } catch (err: any) {
      console.error(`Search error (attempt ${attempt}):`, err);
      if (attempt < 2) {
        console.log("Retrying search...");
        await new Promise(r => setTimeout(r, 1500));
        return doSearch(searchQuery, attempt + 1);
      }
      // Only surface the toast/empty state if we never had cached results.
      if (!usedCache) {
        toast.error("Search failed. Please try again.");
        setResults([]);
      }
    } finally {
      setLoading(false);
      setStreamingMore(false);
    }
  };

  useEffect(() => {
    if (!query) {
      navigate("/");
      return;
    }

    const vague = isVagueQuery(query);

    if (vague) {
      setLoading(false);
      setShowClarify(true);
      // Seed with the static fallback so the UI never flashes empty,
      // then ask Lovable AI for topic-tailored options.
      const fallback = getClarifyingOptions(query);
      setClarifyOptions(fallback);
      setClarifyOptionsLoading(true);
      let cancelled = false;
      (async () => {
        try {
          const { data, error } = await supabase.functions.invoke("clarify-query", {
            body: { query },
          });
          if (cancelled) return;
          const opts = Array.isArray(data?.options) ? data.options.filter((s: any) => typeof s === "string" && s.trim()) : [];
          if (!error && opts.length >= 3) setClarifyOptions(opts.slice(0, 4));
        } catch (e) {
          console.warn("clarify-query invoke failed:", e);
        } finally {
          if (!cancelled) setClarifyOptionsLoading(false);
        }
      })();
      return () => { cancelled = true; };
    }

    setShowClarify(false);
    doSearch(query);
  }, [query, navigate]);

  const handleClarify = (option: string) => {
    setShowClarify(false);
    const refined = `${query} — ${option}`;
    doSearch(refined);
  };

  const handleSave = async (episodeId: string) => {
    if (!user) {
      // Stash a friendly toast and the post-auth redirect target, then send
      // them straight to account creation — no nudge modal in between.
      const redirect = `${location.pathname}${location.search}`;
      toast.message("Create a free account to save episodes ✨");
      navigate(`/login?mode=signup&redirect=${encodeURIComponent(redirect)}`);
      return;
    }
    const nowSaved = await toggleSave(episodeId);
    toast.success(nowSaved ? "Episode saved!" : "Episode removed from saves");
  };

  const handleFeedback = async (episodeId: string, type: "like" | "dislike") => {
    if (!user) {
      setNudge({ open: true, action: type });
      return;
    }
    const result = await toggleFeedback(episodeId, type);
    if (result === "like") toast.success("Thanks! We'll find more like this.");
    else if (result === "dislike") toast.success("Got it — we'll tune your results.");
  };

  const handleFindSimilar = (episode: MockEpisode) => {
    navigate(`/search?q=${encodeURIComponent(`More episodes like "${episode.episodeTitle}"`)}`);
  };

  const relatedQueries = [
    "Best episode on scaling paid ads",
    "Top podcast about email marketing for DTC",
    "How do I reduce churn on subscriptions?",
  ];

  return (
    <div className="min-h-screen flex flex-col retro-grid-bg relative">
      {/* Results-page spotlight: search bar stays in its natural position,
          background blurs only once the user starts typing, and everything
          behind the bar is marked inert while the spotlight is open. */}
      <div
        ref={headerWrapperRef}
        aria-hidden={searchSpotlight}
      >
        <Header compact />
      </div>

      {searchSpotlight && (
        <div
          className="!absolute inset-0 !z-30 pointer-events-none bg-foreground/10 backdrop-blur-sm animate-fade-in"
          aria-hidden="true"
        />
      )}

      <div className="py-2 sm:py-3 relative !z-40">
        <div className="container px-4 sm:px-6">
          <SearchBar
            size="compact"
            initialQuery={query}
            onSpotlightChange={setSearchSpotlight}
            clearOnFocus
          />
        </div>
      </div>


      <main ref={mainRef} className="flex-1 container px-4 sm:px-6 py-4 sm:py-8">
        {loading && (
          <div className="max-w-3xl mx-auto">
            <LoadingState />
            {/* Retro skeleton cards so the page already shows the result silhouette
                while the AI ranking is still in flight. */}
            <div className="mt-6 sm:mt-8 space-y-4 sm:space-y-6" aria-label="Loading results">
              {[1, 2, 3].map((rank) => (
                <ResultCardSkeleton key={rank} rank={rank} delayMs={(rank - 1) * 120} />
              ))}
            </div>
          </div>
        )}

        {showClarify && (
          <ClarifyingQuestion
            query={query}
            options={clarifyOptions.length > 0 ? clarifyOptions : getClarifyingOptions(query)}
            loadingOptions={clarifyOptionsLoading}
            onSelect={handleClarify}
          />
        )}

        {!loading && !showClarify && results.length > 0 && (
          <div className="max-w-3xl mx-auto space-y-4 sm:space-y-6">
            <div className="mb-4 sm:mb-6 animate-fade-in">
              <p className="font-mono text-[10px] sm:text-xs text-muted-foreground uppercase tracking-widest mb-1">
                Top {results.length} episodes for
              </p>
              <h2 className="font-retro font-bold text-base sm:text-xl leading-tight break-words">"{finalQuery}"</h2>
            </div>

            <TiltCard className="hover-lift transition-all duration-200">
              <TrackTopicInline query={finalQuery} />
            </TiltCard>

            <NewsletterCapture
              query={finalQuery}
              theme={theme}
              podcastArtworks={results.slice(0, 3).map((ep) => ep.podcastArtwork).filter(Boolean)}
            />

            {(() => {
              const tier = getMatchedAnswerTier(matchedAnswer?.similarity);
              const showStrong = tier === "strong" && matchedAnswer;
              const showGood = tier === "good" && matchedAnswer;
              const showLoose = tier === "loose" && matchedAnswer;
              return (
                <>
                  {showStrong && (
                    <AnsweredQuestionCallout match={matchedAnswer!} tier="strong" />
                  )}
                  {results.map((ep, i) => (
                    <div key={ep.id} className="space-y-4 sm:space-y-6">
                      <TiltCard>
                        <ResultCard
                          episode={ep}
                          rank={i + 1}
                          onSave={() => handleSave(ep.id)}
                          onFindSimilar={() => handleFindSimilar(ep)}
                          isSaved={savedIds.has(ep.id)}
                          feedback={feedbackMap[ep.id] || null}
                          onFeedback={(type) => handleFeedback(ep.id, type)}
                          searchQuery={finalQuery}
                        />
                      </TiltCard>
                      {showGood && i === 0 && (
                        <AnsweredQuestionCallout match={matchedAnswer!} tier="good" />
                      )}
                    </div>
                  ))}
                  {streamingMore && results.length < 3 &&
                    Array.from({ length: 3 - results.length }).map((_, i) => (
                      <ResultCardSkeleton
                        key={`stream-pl-${i}`}
                        rank={results.length + i + 1}
                        delayMs={i * 120}
                      />
                    ))}
                  {showLoose && (
                    <AnsweredQuestionCallout match={matchedAnswer!} tier="loose" />
                  )}
                </>
              );
            })()}
            <SearchResultsFeedback query={finalQuery} />

            <div className="w-full max-w-2xl mx-auto mt-6 sm:mt-8 animate-fade-in-up" style={{ animationDelay: '500ms' }}>
              <p className="text-xs font-mono text-muted-foreground mb-2 sm:mb-3 flex items-center gap-2">
                <Zap className="w-3.5 h-3.5 text-primary" />
                Related questions:
              </p>
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-2.5 stagger-children">
                {relatedQueries.map((rq) => (
                  <div key={rq} className="inline-block">
                    <button
                      onClick={() => navigate(`/search?q=${encodeURIComponent(rq)}`)}
                      className="btn-comic w-full sm:w-auto justify-start text-left text-xs sm:text-sm px-3 py-3 sm:py-2 min-h-[44px] sm:min-h-0 bg-card text-foreground border-foreground hover:bg-accent hover:text-accent-foreground hover:border-[hsl(195,80%,28%)]"
                    >
                      <span className="relative z-10">{rq}</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center py-3 sm:py-4">
              <p className="font-mono text-xs sm:text-sm text-muted-foreground mb-2">
                Looking for something else? Tell me more.
              </p>
              <SearchBar size="compact" />
            </div>
          </div>
        )}

        {!loading && !showClarify && results.length === 0 && query && (
          <div className="max-w-xl mx-auto text-center py-12">
            <p className="font-mono text-sm text-muted-foreground mb-4">
              No results found for "{query}". This could mean we haven't indexed transcripts for this topic yet.
            </p>
            <SearchBar size="compact" />
          </div>
        )}
      </main>

      <SignupNudge
        open={nudge.open}
        onClose={() => setNudge({ ...nudge, open: false })}
        action={nudge.action}
      />

      <SearchResultsIntroModal ready={!loading && !showClarify && results.length > 0} />
    </div>
  );
}
